import { useEffect } from 'react';
import { Users } from 'lucide-react';
import useVideoStore from '../../stores/videoStore';
import LiveKitVideoGallery from './LiveKitVideoGallery';
import { useTranslation } from '../../i18n';

/**
 * Fallback participant tile (no video, just placeholder)
 */
const FallbackParticipantTile = ({ participant }) => {
  const { t } = useTranslation();
  return (
    <div className="flex-shrink-0 h-12 md:h-20 aspect-video bg-brand-cream rounded-lg flex items-center justify-center border-2 border-brand-primary">
      <div className="text-center">
        {participant.avatarUrl ? (
          <img
            src={participant.avatarUrl}
            alt={participant.name}
            className="w-5 h-5 md:w-8 md:h-8 rounded-full mx-auto md:mb-1 object-cover"
          />
        ) : (
          <div className="w-5 h-5 md:w-8 md:h-8 bg-brand-light rounded-full mx-auto md:mb-1 flex items-center justify-center">
            <Users size={12} className="text-brand-primary md:hidden" />
            <Users size={16} className="text-brand-primary hidden md:block" />
          </div>
        )}
        <p className="hidden md:block text-[10px] text-brand-primary font-medium truncate max-w-[80px]">
          {participant.role === 'teacher' ? t('video.teacher') : participant.name}
        </p>
      </div>
    </div>
  );
};

/**
 * Smart Video Gallery - shows real video when connected, placeholders otherwise
 * When LiveKit is connected, shows LiveKitVideoGallery with real video streams
 *
 * On mobile (< md), only the participant at `mobileActiveIndex` is shown so that
 * the user can cycle participants one-at-a-time via parent-provided arrow buttons.
 * On desktop (>= md), all participants are rendered as before.
 */
const SmartVideoGallery = ({ classroomParticipants = [], mobileActiveIndex = 0, onCountChange }) => {
  const { t } = useTranslation();
  const isVideoConnected = useVideoStore(s => s.isConnected);

  // Notify parent of participant count when LiveKit is not connected
  // (LiveKitVideoGallery reports its own count when connected)
  useEffect(() => {
    if (!isVideoConnected) {
      onCountChange?.(classroomParticipants.length);
    }
  }, [classroomParticipants.length, isVideoConnected, onCountChange]);

  // If connected to LiveKit, show real video gallery
  if (isVideoConnected) {
    return (
      <LiveKitVideoGallery
        mobileActiveIndex={mobileActiveIndex}
        onCountChange={onCountChange}
      />
    );
  }

  // Show placeholders when not connected
  if (classroomParticipants.length === 0) {
    return (
      <div className="flex-shrink-0 h-12 md:h-20 aspect-video bg-brand-cream rounded-lg flex items-center justify-center border-2 border-brand-primary">
        <div className="text-center">
          <div className="w-5 h-5 md:w-8 md:h-8 bg-brand-light rounded-full mx-auto md:mb-1 flex items-center justify-center">
            <Users size={12} className="text-brand-primary md:hidden" />
            <Users size={16} className="text-brand-primary hidden md:block" />
          </div>
          <p className="hidden md:block text-[10px] text-brand-taupe font-medium">{t('video.connecting')}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {classroomParticipants.map((participant, index) => (
        <div
          key={participant.id || participant.name}
          className={index === mobileActiveIndex ? 'contents' : 'hidden md:contents'}
        >
          <FallbackParticipantTile participant={participant} />
        </div>
      ))}
    </>
  );
};

export default SmartVideoGallery;
